import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  formatHash,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

const eventConstructorKeys = [
  'AT_TARGET',
  'BUBBLING_PHASE',
  'CAPTURING_PHASE',
  'NONE',
  'length',
  'name',
  'prototype',
];

const eventPrototypeKeys = [
  'AT_TARGET',
  'BUBBLING_PHASE',
  'CAPTURING_PHASE',
  'NONE',
  'bubbles',
  'cancelBubble',
  'cancelable',
  'composed',
  'composedPath',
  'constructor',
  'currentTarget',
  'defaultPrevented',
  'eventPhase',
  'initEvent',
  'preventDefault',
  'returnValue',
  'srcElement',
  'stopImmediatePropagation',
  'stopPropagation',
  'target',
  'timeStamp',
  'type',
];

const eventInstanceKeys = ['isTrusted'];
const eventInitKeys = ['bubbles', 'cancelable', 'composed'];
const eventValueKeys = [
  'bubbles',
  'cancelable',
  'composed',
  'isTrusted',
  'type',
];

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort();
}

function eventShape(event: Event): RawData {
  return {
    ownKeys: Object.getOwnPropertyNames(event).sort(),
    values: Object.fromEntries(
      eventValueKeys.map((key) => [key, Reflect.get(event, key)]),
    ),
  };
}

function constructEvent(...args: unknown[]): Event {
  return Reflect.construct(Event, args) as Event;
}

function proxyInitReads(): { event: Event; reads: string[] } {
  const reads: string[] = [];
  const init = new Proxy(
    {},
    {
      get(_target, property): boolean {
        reads.push(String(property));
        return true;
      },
    },
  );
  return {
    event: constructEvent('proxy-init', init),
    reads: uniqueSorted(reads),
  };
}

function collect(): RawData {
  const proxyInit = proxyInitReads();
  const createEvent = Reflect.get(document, 'createEvent') as (
    interfaceName: string,
  ) => Event;
  const legacy = Reflect.apply(createEvent, document, ['Event']);
  const initEvent = Reflect.get(legacy, 'initEvent') as (
    type: string,
    bubbles?: boolean,
    cancelable?: boolean,
  ) => void;
  Reflect.apply(initEvent, legacy, ['legacy-init', true, true]);
  return {
    Event: {
      constructorKeys: Object.getOwnPropertyNames(Event).sort(),
      prototypeKeys: Object.getOwnPropertyNames(Event.prototype).sort(),
      typeOnly: eventShape(constructEvent('type-only')),
      undefinedInit: eventShape(constructEvent('undefined-init', undefined)),
      nullInit: eventShape(constructEvent('null-init', null)),
      emptyInit: eventShape(constructEvent('empty-init', {})),
      populatedInit: eventShape(
        constructEvent('populated-init', {
          bubbles: true,
          cancelable: true,
          composed: true,
        }),
      ),
      proxyInit: eventShape(proxyInit.event),
      legacyInit: eventShape(legacy),
    },
    EventInit: {
      proxyReads: proxyInit.reads,
    },
  };
}

function stringArray(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((item) => typeof item == 'string')
    ? value
    : null;
}

function requireKeys(
  value: unknown,
  expected: readonly string[],
  label: string,
  reasons: string[],
): void {
  const keys = stringArray(value);
  if (!keys || keys.length != expected.length) {
    reasons.push(`${label} fields differ from stock Chrome`);
    return;
  }
  for (let index = 0; index < expected.length; index += 1) {
    if (keys[index] != expected[index]) {
      reasons.push(`${label} fields differ from stock Chrome`);
      return;
    }
  }
}

export function eventSurfaceReasons(rawData: RawData): string[] {
  const reasons: string[] = [];
  const event = isRecord(rawData.Event) ? rawData.Event : null;
  const eventInit = isRecord(rawData.EventInit) ? rawData.EventInit : null;
  if (!event || !eventInit) {
    return ['Event surface has an invalid shape'];
  }

  requireKeys(
    event.constructorKeys,
    eventConstructorKeys,
    'Event constructor',
    reasons,
  );
  requireKeys(
    event.prototypeKeys,
    eventPrototypeKeys,
    'Event prototype',
    reasons,
  );
  for (const name of [
    'typeOnly',
    'undefinedInit',
    'nullInit',
    'emptyInit',
    'populatedInit',
    'proxyInit',
    'legacyInit',
  ]) {
    const instance = isRecord(event[name]) ? event[name] : null;
    requireKeys(instance?.ownKeys, eventInstanceKeys, `Event ${name}`, reasons);
  }
  requireKeys(
    eventInit.proxyReads,
    eventInitKeys,
    'EventInit proxy reads',
    reasons,
  );
  return reasons;
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validation(eventSurfaceReasons(rawData));
}

export const eventSurfaceTest: TestDefinition = {
  id: 'eventSurface',
  title: 'Event surface',
  category: 'shared',
  format,
  run,
  validate,
};
